import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { CheckDone01 } from "@untitledui/icons";
import { ActivityFeed } from "@/components/application/activity-feed/activity-feed";
import { ExpandableActivityCard } from "@/components/application/containers/timeline/expandable-activity-card";
import { ActivityFiltersBar } from "@/components/application/activity-feed/activity-filters";
import { TradeCommsView } from "@/components/application/trade-comms";
import { Timeline } from "@/components/application/timeline";
import type { TimelineEntry } from "@/components/application/timeline";
import { useInfiniteScroll } from "@/components/application/containers/use-infinite-scroll";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { ButtonGroup, ButtonGroupItem } from "@/components/base/button-group/button-group";
import {
    useActivities,
    useActivitiesByCategory,
    useActivityUnreadCount,
    useMarkActivityAsRead,
    useMarkAllActivitiesAsRead,
} from "@/hooks/api/use-activities";
import { useEmailCounts } from "@/hooks/api/use-email-accounts";
import { mapActivitiesToFrontend } from "@/utils/activity-mapper";
import type { ActivityFilters, ActivityItem } from "@/types/activity";
import { useTranslation } from "react-i18next";

const USE_MOCK_DATA = import.meta.env.VITE_USE_MOCK_DATA === "true";
const DEPTH_DEFAULT = 50;
const DEPTH_MAX = 200;
const DEPTH_STEP = 50;

type ActivitySection = "trade-comms" | "system-logs";
type SystemLogsView = "carousel" | "timeline";

function getSectionFromParams(searchParams: URLSearchParams): ActivitySection {
    const tab = searchParams.get("tab");
    return tab === "system-logs" || tab === "trade-comms" ? tab : "trade-comms";
}

function getViewFromParams(searchParams: URLSearchParams): SystemLogsView {
    const view = searchParams.get("view");
    return view === "timeline" || view === "carousel" ? view : "carousel";
}

function getDepthFromParams(searchParams: URLSearchParams): number {
    const d = searchParams.get("depth");
    if (d == null) return DEPTH_DEFAULT;
    const n = parseInt(d, 10);
    if (Number.isNaN(n)) return DEPTH_DEFAULT;
    return Math.min(DEPTH_MAX, Math.max(DEPTH_DEFAULT, n));
}

export const ReceiverActivityPage = () => {
    const { t } = useTranslation(["pages:activity", "common"]);
    const [searchParams, setSearchParams] = useSearchParams();
    const [filters, setFilters] = useState<ActivityFilters>({ readState: "all" });
    const [section, setSection] = useState<ActivitySection>(() => getSectionFromParams(searchParams));
    const [view, setView] = useState<SystemLogsView>(() => getViewFromParams(searchParams));
    const depth = getDepthFromParams(searchParams);

    useEffect(() => {
        setSection(getSectionFromParams(searchParams));
        setView(getViewFromParams(searchParams));
    }, [searchParams]);

    const apiFiltersForTimeline = useMemo(
        () => ({
            is_read:
                filters.readState === "all" ? undefined : filters.readState === "read",
            from_date: filters.fromDate?.toISOString(),
            to_date: filters.toDate?.toISOString(),
            keyword: filters.keyword,
            limit: depth,
            skip: 0,
        }),
        [filters.readState, filters.fromDate, filters.toDate, filters.keyword, depth]
    );

    const {
        items: systemActivitiesFromApi,
        rawActivitiesMap,
        isLoading: isLoadingCarousel,
        isError: isErrorCarousel,
        error: errorCarousel,
        hasMoreForCategory,
        onLoadMoreForCategory,
        isLoadingMoreForCategory,
    } = useActivitiesByCategory(
        "receiver",
        {
            readState: filters.readState,
            fromDate: filters.fromDate,
            toDate: filters.toDate,
            keyword: filters.keyword,
        },
        {
            enabled: !USE_MOCK_DATA && section === "system-logs" && view === "carousel",
            refetchInterval: 30000,
        }
    );

    const {
        data: timelineApiResponse,
        isLoading: isLoadingTimeline,
        isError: isErrorTimeline,
        error: errorTimeline,
    } = useActivities("receiver", apiFiltersForTimeline, {
        enabled: !USE_MOCK_DATA && section === "system-logs" && view === "timeline",
        refetchInterval: 30000,
    });

    const systemActivitiesForCarousel = useMemo(() => {
        if (USE_MOCK_DATA) {
            const { activityService } = require("@/data/mock-activities");
            const activities = activityService.getByAudience("receiver");
            return activities.filter((a: ActivityItem) => a.activityCategory !== "email");
        }
        return systemActivitiesFromApi;
    }, [USE_MOCK_DATA, systemActivitiesFromApi]);

    const systemActivitiesForTimeline = useMemo(() => {
        if (!timelineApiResponse?.activities) return [];
        const mapped = mapActivitiesToFrontend(timelineApiResponse.activities);
        return mapped.filter((a: ActivityItem) => a.activityCategory !== "email");
    }, [timelineApiResponse?.activities]);

    const timelineRawMap = useMemo(() => {
        const map: Record<string, import("@/data/api/activities").BackendActivity> = {};
        if (timelineApiResponse?.activities) {
            for (const raw of timelineApiResponse.activities) {
                map[raw.activity_id] = raw;
            }
        }
        return map;
    }, [timelineApiResponse?.activities]);

    const systemActivities =
        view === "timeline" ? systemActivitiesForTimeline : systemActivitiesForCarousel;

    const { mutate: markAsRead } = useMarkActivityAsRead();
    const { mutate: markAllAsRead, isPending: isMarkingAll } = useMarkAllActivitiesAsRead("receiver");
    const { data: totalUnreadCount = 0 } = useActivityUnreadCount("receiver", {
        enabled: !USE_MOCK_DATA,
        refetchInterval: 60000,
    });
    const { data: emailCounts } = useEmailCounts({
        enabled: !USE_MOCK_DATA,
        refetchInterval: 60000,
    });
    const unreadEmailCount = emailCounts?.inbox_unread_total ?? 0;
    const unreadSystemCount = useMemo(
        () => systemActivities.filter((a: ActivityItem) => !a.isRead).length,
        [systemActivities]
    );

    const handleMarkAsRead = useCallback(
        (activityId: string) => {
            if (!USE_MOCK_DATA) markAsRead({ activityId });
        },
        [markAsRead]
    );

    const timelineEntries = useMemo((): TimelineEntry[] => {
        const sorted = [...systemActivitiesForTimeline].sort(
            (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
        );
        let currentPosition: "left" | "right" = "left";
        let lastCategory: string | undefined;
        return sorted.map((item) => {
            const category = item.activityCategory ?? "other";
            if (lastCategory !== undefined && category !== lastCategory) {
                currentPosition = currentPosition === "left" ? "right" : "left";
            }
            lastCategory = category;
            return {
                id: item.id,
                date: item.timestamp,
                content: (() => {
                    const raw = timelineRawMap[item.id];
                    if (raw) return <ExpandableActivityCard activity={raw} isContainerContext={false} />;
                    return null;
                })(),
                position: currentPosition,
            };
        });
    }, [systemActivitiesForTimeline, timelineRawMap, handleMarkAsRead]);

    const hasMoreTimeline =
        (timelineApiResponse?.total ?? 0) > systemActivitiesForTimeline.length && depth < DEPTH_MAX;
    const loadMoreTimeline = useCallback(() => {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                const current = getDepthFromParams(next);
                next.set("depth", String(Math.min(DEPTH_MAX, current + DEPTH_STEP)));
                return next;
            },
            { replace: true }
        );
    }, [setSearchParams]);
    const { loadMoreRef } = useInfiniteScroll({
        hasMore: hasMoreTimeline,
        loadMore: loadMoreTimeline,
        threshold: 200,
    });

    const handleMarkAllAsRead = () => {
        if (!USE_MOCK_DATA && totalUnreadCount > 0) {
            markAllAsRead();
        }
    };

    const displayTotal = section === "system-logs" ? systemActivities.length : 0;

    const isLoadingSystemLogs =
        (view === "carousel" && isLoadingCarousel) || (view === "timeline" && isLoadingTimeline);
    const isErrorSystemLogs =
        (view === "carousel" && isErrorCarousel) || (view === "timeline" && isErrorTimeline);
    const errorSystemLogs = view === "carousel" ? errorCarousel : errorTimeline;

    if (isLoadingSystemLogs && !USE_MOCK_DATA && section === "system-logs") {
        return (
            <div className="space-y-6">
                <div>
                    <h1 className="text-2xl font-semibold text-primary">{t("receiver.title")}</h1>
                    <p className="text-tertiary mt-1">{t("receiver.subtitle")}</p>
                </div>
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-secondary" />
                </div>
            </div>
        );
    }

    if (isErrorSystemLogs && !USE_MOCK_DATA && section === "system-logs") {
        return (
            <div className="space-y-6">
                <div>
                    <h1 className="text-2xl font-semibold text-primary">{t("receiver.title")}</h1>
                    <p className="text-tertiary mt-1">{t("receiver.subtitle")}</p>
                </div>
                <div className="rounded-xl bg-error-50 dark:bg-error-950 p-6 text-error-600 dark:text-error-400">
                    {t("common:error.loadingFailed", { defaultValue: "Errore nel caricamento delle attività" })}
                    {errorSystemLogs instanceof Error && <p className="mt-2 text-sm opacity-75">{errorSystemLogs.message}</p>}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-primary">{t("receiver.title")}</h1>
                    <p className="text-tertiary mt-1">{t("receiver.subtitle")}</p>
                </div>
                <div className="flex items-center gap-4">
                    {!USE_MOCK_DATA && totalUnreadCount > 0 && (
                        <Button color="tertiary" size="sm" iconLeading={CheckDone01} onClick={handleMarkAllAsRead} isLoading={isMarkingAll}>
                            {t("actions.markAllAsRead")}
                        </Button>
                    )}
                    {!USE_MOCK_DATA && section === "system-logs" && (
                        <div className="text-sm text-tertiary">
                            {displayTotal} {t("common:activities", { defaultValue: "attività" })}
                        </div>
                    )}
                </div>
            </div>

            <ButtonGroup
                selectedKeys={new Set([section])}
                onSelectionChange={(keys) => {
                    const k = Array.from(keys as Set<string>)[0];
                    if (k === "trade-comms" || k === "system-logs") {
                        setSection(k);
                        setSearchParams((prev) => {
                            const next = new URLSearchParams(prev);
                            next.set("tab", k);
                            return next;
                        }, { replace: true });
                    }
                }}
            >
                <ButtonGroupItem id="trade-comms">
                    {t("tradeComms.title", { defaultValue: "Trade Communications" })}
                    {unreadEmailCount > 0 && (
                        <Badge type="pill-color" size="sm" color="brand" className="ml-1">
                            {unreadEmailCount}
                        </Badge>
                    )}
                </ButtonGroupItem>
                <ButtonGroupItem id="system-logs">
                    {t("systemLogs.title", { defaultValue: "System Activity Logs" })}
                    {unreadSystemCount > 0 && (
                        <Badge type="pill-color" size="sm" color="gray" className="ml-1">
                            {unreadSystemCount}
                        </Badge>
                    )}
                </ButtonGroupItem>
            </ButtonGroup>

            {section === "trade-comms" ? (
                <TradeCommsView audience="receiver" onMarkActivityAsRead={handleMarkAsRead} />
            ) : (
                <>
                    <ActivityFiltersBar value={filters} onChange={setFilters} />
                    {section === "system-logs" && (
                        <ButtonGroup
                            selectedKeys={new Set([view])}
                            onSelectionChange={(keys) => {
                                const k = Array.from(keys as Set<string>)[0];
                                if (k === "carousel" || k === "timeline") {
                                    setView(k);
                                    setSearchParams((prev) => {
                                        const next = new URLSearchParams(prev);
                                        next.set("view", k);
                                        if (k === "timeline" && !next.has("depth")) next.set("depth", String(DEPTH_DEFAULT));
                                        return next;
                                    }, { replace: true });
                                }
                            }}
                        >
                            <ButtonGroupItem id="carousel">
                                {t("systemLogs.viewCarousel", { defaultValue: "Carousel" })}
                            </ButtonGroupItem>
                            <ButtonGroupItem id="timeline">
                                {t("systemLogs.viewTimeline", { defaultValue: "Timeline" })}
                            </ButtonGroupItem>
                        </ButtonGroup>
                    )}
                    {view === "carousel" ? (
                        <ActivityFeed
                            items={systemActivities}
                            filters={filters}
                            renderItem={(item) => {
                                const raw = rawActivitiesMap[item.id];
                                if (raw) return <ExpandableActivityCard activity={raw} isContainerContext={false} />;
                                return null;
                            }}
                            onLoadMoreForCategory={onLoadMoreForCategory}
                            hasMoreForCategory={hasMoreForCategory}
                            isLoadingMoreForCategory={isLoadingMoreForCategory}
                        />
                    ) : (
                        <div className="rounded-xl bg-primary p-0 shadow-xs ring-1 ring-secondary">
                            <div className="px-4 py-4 sm:px-6">
                                <Timeline items={timelineEntries} order="desc" />
                                <div ref={loadMoreRef} className="h-4 w-4" aria-hidden />
                                {timelineEntries.length > 0 && hasMoreTimeline && isLoadingTimeline && (
                                    <div className="flex justify-center py-4">
                                        <div className="h-6 w-6 animate-spin rounded-full border-2 border-secondary border-t-brand-solid" />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ReceiverActivityPage;
